import type { CreateCollaboratorRequest, UpdateCollaboratorRequest, CollaboratorDto } from '@/api/ApiClient';
import { eppnFormRowsToDto, type EppnFormRow } from '@/lib/users/collaboratorEppnForm';
import {
  inputDateToApiDate,
  isCurrentOpenTarget,
  type TimesliceEditTarget,
} from '@/lib/users/collaboratorTimesliceForm';

/** Grid / customizing row may add `userType`; API fields match `CollaboratorDto` (e.g. `orcId`). */
export type UserCollaboratorDialogRecord = CollaboratorDto & {
  userType?: 'internal' | 'external';
};

const emptyToUndef = (s: string): string | undefined => {
  const t = s.trim();
  return t === '' ? undefined : t;
};

/**
 * Maps dialog form state and edit/create rules (in-house vs external, hidden fields) to the API request body.
 */
export function buildCollaboratorRequest(params: {
  isEdit: boolean;
  isInhouseUser: boolean;
  record: UserCollaboratorDialogRecord | null;
  editTarget?: TimesliceEditTarget;
  effectiveFromInput?: string;
  form: {
    eppnRows: EppnFormRow[];
    firstName: string;
    middleName: string;
    lastName: string;
    abbreviation: string;
    orcId: string;
    faculty: string | undefined;
    roleId: number;
    techniques: string[];
    managedInstruments: string[];
    position: string;
    institution: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    email: string;
    phone: string;
    isAdmin: boolean;
    inactive: boolean;
    teamsUserId: string;
  };
}): CreateCollaboratorRequest | UpdateCollaboratorRequest {
  const { isEdit, isInhouseUser, record, form, editTarget, effectiveFromInput } = params;
  const roleIds = [form.roleId];
  const editingHistoricalSlice = isEdit && editTarget != null && !isCurrentOpenTarget(editTarget, record ?? undefined);

  let institution = emptyToUndef(form.institution);
  let city = emptyToUndef(form.city);
  let state = emptyToUndef(form.state);
  let zip = emptyToUndef(form.zip);
  /** Blank / whitespace-only country defaults to USA (matches dialog + DB expectations). */
  let country: string = emptyToUndef(form.country) ?? 'USA';
  let faculty = form.faculty != null ? emptyToUndef(form.faculty) : undefined;
  let techniques = [...form.techniques];
  let managedInstruments = [...form.managedInstruments];
  let isAdmin = form.isAdmin;

  if (isEdit && record && !editingHistoricalSlice) {
    if (isInhouseUser) {
      institution = emptyToUndef(String(record.institution ?? ''));
      city = emptyToUndef(String(record.city ?? ''));
      state = emptyToUndef(String(record.state ?? ''));
      zip = emptyToUndef(String(record.zip ?? ''));
      country = emptyToUndef(String(record.country ?? '')) ?? 'USA';
    } else {
      faculty = emptyToUndef(String(record.faculty ?? ''));
      techniques = Array.isArray(record.techniques) ? [...record.techniques] : [];
      managedInstruments = Array.isArray(record.managedInstruments) ? [...record.managedInstruments] : [];
      isAdmin = Boolean(record.isAdmin);
    }
  } else if (!isEdit) {
    if (isInhouseUser) {
      institution = undefined;
      city = undefined;
      state = undefined;
      zip = undefined;
      country = 'USA';
    } else {
      faculty = undefined;
      techniques = [];
      managedInstruments = [];
      isAdmin = false;
    }
  }

  const base: CreateCollaboratorRequest = {
    eppns: eppnFormRowsToDto(form.eppnRows),
    firstName: emptyToUndef(form.firstName),
    middleName: emptyToUndef(form.middleName),
    lastName: emptyToUndef(form.lastName),
    abbreviation: emptyToUndef(form.abbreviation),
    orcId: emptyToUndef(form.orcId),
    email: emptyToUndef(form.email),
    phone: emptyToUndef(form.phone),
    position: emptyToUndef(form.position),
    institution,
    city,
    state,
    zip,
    country,
    faculty,
    roleIds,
    techniques,
    managedInstruments,
    isAdmin,
    inactive: form.inactive,
    teamsUserId: emptyToUndef(form.teamsUserId),
  };

  if (isEdit && record?.id) {
    const update: UpdateCollaboratorRequest = {
      ...base,
      id: record.id,
    };

    if (editingHistoricalSlice && editTarget?.kind === 'historical') {
      update.validFrom = editTarget.validFrom;
      update.validTo = editTarget.validTo;
    } else {
      update.validFrom = record.validFrom;
      update.validTo = record.validTo;
      const effectiveFrom = inputDateToApiDate(effectiveFromInput);
      if (effectiveFrom) {
        update.effectiveFrom = effectiveFrom;
      }
    }

    return update;
  }

  return base;
}
